import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import {
  PublicReview,
  OwnReview,
  CreateReviewData,
  UpdateReviewData,
  ProductReviewStats,
  ReviewPage,
} from '../../types';
import { ReviewError, toReviewError } from '../../utils/reviewErrors';

export const REVIEWS_PAGE_SIZE = 10;

interface ReviewsState {
  /** A termék jóváhagyott értékelései (a „Továbbiak" gombbal oldalanként bővül). */
  reviews: PublicReview[];
  /** Szerveroldali összesen (a fejléc darabszáma) — nem a betöltött oldal hossza. */
  totalCount: number;
  page: number;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  stats: ProductReviewStats | null;
  /** A bejelentkezett felhasználó saját értékelése (bármilyen állapotban). */
  own: OwnReview | null;
  ownLoaded: boolean;
  submitting: boolean;
  formError: ReviewError | null;
  /** Az utolsó lista-kérés azonosítója — az elavult (pl. rendezés-váltás előtti) választ eldobjuk. */
  listRequestId: string | null;
  /** Az éppen mutatott termék — a más termékre szóló késői válaszokat eldobjuk. */
  productId: number | null;
  statsRequestId: string | null;
  ownRequestId: string | null;
}

const initialState: ReviewsState = {
  reviews: [],
  totalCount: 0,
  page: 0,
  hasMore: false,
  loading: false,
  loadingMore: false,
  error: null,
  stats: null,
  own: null,
  ownLoaded: false,
  submitting: false,
  formError: null,
  listRequestId: null,
  productId: null,
  statsRequestId: null,
  ownRequestId: null,
};

const LIST_ERROR = 'Nem sikerült betölteni az értékeléseket.';

export interface FetchReviewsArgs {
  productId: number;
  ordering?: string;
  page?: number;
}

export const fetchProductReviews = createAsyncThunk<
  ReviewPage<PublicReview>,
  FetchReviewsArgs,
  { rejectValue: ReviewError }
>('reviews/fetchProductReviews', async ({ productId, ordering = '-created_at', page = 1 }, { rejectWithValue }) => {
  try {
    const response = await api.get<ReviewPage<PublicReview>>('/reviews/', {
      params: { product_id: productId, ordering, page, page_size: REVIEWS_PAGE_SIZE },
    });
    return response.data;
  } catch (error: unknown) {
    return rejectWithValue(toReviewError(error));
  }
});

export const fetchReviewStats = createAsyncThunk<ProductReviewStats, number, { rejectValue: ReviewError }>(
  'reviews/fetchReviewStats',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await api.get<ProductReviewStats>(`/products/${productId}/review-stats/`);
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(toReviewError(error));
    }
  },
);

export const fetchMyReview = createAsyncThunk<OwnReview | null, number, { rejectValue: ReviewError }>(
  'reviews/fetchMyReview',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await api.get<{ review: OwnReview | null }>('/reviews/mine/', {
        params: { product_id: productId },
      });
      return response.data?.review ?? null;
    } catch (error: unknown) {
      return rejectWithValue(toReviewError(error));
    }
  },
);

export const createReview = createAsyncThunk<OwnReview, CreateReviewData, { rejectValue: ReviewError }>(
  'reviews/createReview',
  async (reviewData, { rejectWithValue }) => {
    try {
      const response = await api.post<OwnReview>('/reviews/', reviewData);
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(toReviewError(error));
    }
  },
);

export const updateReview = createAsyncThunk<
  OwnReview,
  { id: number; data: UpdateReviewData },
  { rejectValue: ReviewError }
>('reviews/updateReview', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await api.patch<OwnReview>(`/reviews/${id}/`, data);
    return response.data;
  } catch (error: unknown) {
    return rejectWithValue(toReviewError(error));
  }
});

export const deleteReview = createAsyncThunk<number, number, { rejectValue: ReviewError }>(
  'reviews/deleteReview',
  async (reviewId, { rejectWithValue }) => {
    try {
      await api.delete(`/reviews/${reviewId}/`);
      return reviewId;
    } catch (error: unknown) {
      return rejectWithValue(toReviewError(error));
    }
  },
);

export interface HelpfulResult {
  reviewId: number;
  helpful_count: number;
  has_voted: boolean;
}

export const markReviewHelpful = createAsyncThunk<HelpfulResult, number, { rejectValue: ReviewError }>(
  'reviews/markReviewHelpful',
  async (reviewId, { rejectWithValue }) => {
    try {
      const response = await api.post<{ helpful_count: number; has_voted?: boolean }>(
        `/reviews/${reviewId}/helpful/`,
      );
      return { reviewId, helpful_count: response.data.helpful_count, has_voted: response.data.has_voted ?? true };
    } catch (error: unknown) {
      const parsed = toReviewError(error);
      if (parsed.status === 404) {
        return rejectWithValue({ ...parsed, message: 'Ez az értékelés már nem érhető el.' });
      }
      return rejectWithValue(parsed);
    }
  },
);

/** A válasz a legutóbbi kérésé és a mostani termékre szól-e. */
function isCurrent(state: ReviewsState, latestId: string | null, requestId: string, productId: number): boolean {
  return latestId === requestId && state.productId === productId;
}

const reviewsSlice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    /** Termék-váltás: tiszta lap az új termékkel (a régi termék késői válaszai ezután eldobódnak). */
    setReviewProduct: (state, action: PayloadAction<number | null>) => {
      Object.assign(state, initialState, { productId: action.payload });
    },
    /** Kijelentkezés / felhasználó-váltás: a saját értékelés törlése. */
    resetOwn: (state) => {
      state.own = null;
      state.ownLoaded = false;
      state.ownRequestId = null;
    },
    clearReviews: (state) => {
      state.productId = null;
      state.statsRequestId = null;
      state.ownRequestId = null;
      state.reviews = [];
      state.totalCount = 0;
      state.page = 0;
      state.hasMore = false;
      state.stats = null;
      state.own = null;
      state.ownLoaded = false;
      state.error = null;
      state.formError = null;
      state.listRequestId = null;
    },
    clearError: (state) => {
      state.error = null;
      state.formError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Lista (1. oldal: csere; további oldal: hozzáfűzés)
      .addCase(fetchProductReviews.pending, (state, action) => {
        state.listRequestId = action.meta.requestId;
        state.error = null;
        if ((action.meta.arg.page ?? 1) > 1) state.loadingMore = true;
        else state.loading = true;
      })
      .addCase(fetchProductReviews.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        if (state.productId !== null && action.meta.arg.productId !== state.productId) return;
        const page = action.meta.arg.page ?? 1;
        const results = Array.isArray(action.payload?.results) ? action.payload.results : [];
        if (page > 1) {
          const seen = new Set(state.reviews.map((r) => r.id));
          state.reviews.push(...results.filter((r) => !seen.has(r.id)));
        } else {
          state.reviews = results;
        }
        state.totalCount = action.payload?.count ?? state.reviews.length;
        state.page = page;
        state.hasMore = !!action.payload?.next;
        state.loading = false;
        state.loadingMore = false;
      })
      .addCase(fetchProductReviews.rejected, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload?.message || LIST_ERROR;
      })

      // Statisztika (átlag, eloszlás) — csendes hiba: az összesítő egyszerűen nem látszik.
      .addCase(fetchReviewStats.pending, (state, action) => {
        state.statsRequestId = action.meta.requestId;
      })
      .addCase(fetchReviewStats.fulfilled, (state, action) => {
        if (!isCurrent(state, state.statsRequestId, action.meta.requestId, action.meta.arg)) return;
        state.stats = action.payload;
      })

      // Saját értékelés — csak a legutóbbi kérés, és csak a mostani termékre.
      .addCase(fetchMyReview.pending, (state, action) => {
        state.ownRequestId = action.meta.requestId;
      })
      .addCase(fetchMyReview.fulfilled, (state, action) => {
        if (!isCurrent(state, state.ownRequestId, action.meta.requestId, action.meta.arg)) return;
        state.own = action.payload;
        state.ownLoaded = true;
      })
      .addCase(fetchMyReview.rejected, (state, action) => {
        if (!isCurrent(state, state.ownRequestId, action.meta.requestId, action.meta.arg)) return;
        state.own = null;
        state.ownLoaded = true;
      })

      // Létrehozás / szerkesztés
      .addCase(createReview.pending, (state) => {
        state.submitting = true;
        state.formError = null;
      })
      .addCase(createReview.fulfilled, (state, action) => {
        state.submitting = false;
        if (action.payload.product !== state.productId) return;
        state.own = action.payload;
        state.ownLoaded = true;
      })
      .addCase(createReview.rejected, (state, action) => {
        state.submitting = false;
        state.formError = action.payload ?? { message: 'Az értékelés beküldése nem sikerült.', fieldErrors: {} };
      })
      .addCase(updateReview.pending, (state) => {
        state.submitting = true;
        state.formError = null;
      })
      .addCase(updateReview.fulfilled, (state, action) => {
        state.submitting = false;
        if (action.payload.product !== state.productId) return;
        state.own = action.payload;
        // A módosított értékelés újra moderálásra kerül — a publikus listából kikerül.
        if (action.payload.status !== 'approved') {
          const before = state.reviews.length;
          state.reviews = state.reviews.filter((r) => r.id !== action.payload.id);
          state.totalCount = Math.max(0, state.totalCount - (before - state.reviews.length));
        }
      })
      .addCase(updateReview.rejected, (state, action) => {
        state.submitting = false;
        state.formError = action.payload ?? { message: 'Az értékelés módosítása nem sikerült.', fieldErrors: {} };
      })

      // Törlés
      .addCase(deleteReview.fulfilled, (state, action) => {
        const before = state.reviews.length;
        state.reviews = state.reviews.filter((r) => r.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - (before - state.reviews.length));
        if (state.own?.id === action.payload) state.own = null;
      })

      // Hasznos
      .addCase(markReviewHelpful.fulfilled, (state, action) => {
        const review = state.reviews.find((r) => r.id === action.payload.reviewId);
        if (review) {
          review.helpful_count = action.payload.helpful_count;
          review.has_voted = action.payload.has_voted;
        }
      })

      // Kijelentkezés: a saját értékelés nem maradhat a másik (vagy anonim) felhasználónál.
      .addMatcher(
        (action) => action.type === 'auth/logout/fulfilled',
        (state) => {
          state.own = null;
          state.ownLoaded = false;
          state.ownRequestId = null;
        },
      );
  },
});

export const { clearReviews, clearError, setReviewProduct, resetOwn } = reviewsSlice.actions;

export default reviewsSlice.reducer;
